import { useEffect, useState } from 'react';

import { api } from '@/services/api';

interface FrontendVersion {
  version: string;
  built: string;
  commit: string;
}

interface BackendVersion {
  version: string;
  build_date: string;
  git_commit: string;
}

export interface VersionInfo {
  frontend: FrontendVersion | null;
  backend: BackendVersion | null;
  backendStatus: 'connecting' | 'connected' | 'disconnected';
}

export function useVersionInfo(): VersionInfo {
  const [frontend, setFrontend] = useState<FrontendVersion | null>(null);
  const [backend, setBackend] = useState<BackendVersion | null>(null);
  const [backendStatus, setBackendStatus] =
    useState<VersionInfo['backendStatus']>('connecting');

  useEffect(() => {
    // FA-2 (2026-05-27): cache: 'no-store' bypasses browser HTTP cache.
    // Without it, after a deploy the browser may serve a stale /version.json
    // from disk cache (nginx's response carried no Cache-Control: no-cache
    // pre-fix → default heuristics + ETag/Last-Modified made it cacheable).
    // The nginx-side fix lives in docker/nginx.conf as `location = /version.json`
    // with no-cache headers; this is the SPA-side belt-and-suspenders.
    fetch('/version.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then(setFrontend)
      .catch(() => setFrontend(null));

    // Routed through services/api.ts (not bare fetch): the helper supplies
    // Authorization: Bearer from localStorage and throws ApiError on non-2xx,
    // so the .catch below maps any failure → 'disconnected' (same as the
    // prior `if (!r.ok) throw` branch).
    api
      .get<{ version?: string; build_date?: string; git_commit?: string }>('/health')
      .then((data) => {
        setBackend({
          version: data.version ?? 'unknown',
          build_date: data.build_date ?? 'unknown',
          git_commit: data.git_commit ?? 'unknown',
        });
        setBackendStatus('connected');
      })
      .catch(() => setBackendStatus('disconnected'));
  }, []);

  return { frontend, backend, backendStatus };
}
